import React from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * DeepAnalysisErrorBoundary
 * --------------------------
 * Hard isolation boundary around the Deep Analysis feature so that a
 * runtime error inside the (long-running, polling, async) Deep Analysis
 * subtree CANNOT bubble up and break unrelated parts of the Stock Detail
 * page (Target Price card, Valuations, etc.).
 *
 * Critical rules:
 *  1. Never propagate an error to the parent.
 *  2. Never wedge into a permanent error state — provide an in-place
 *     "Try again" button that resets the boundary.
 *  3. Reset automatically if the symbol prop changes (so navigating to
 *     a different stock heals the section).
 */
class DeepAnalysisErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps) {
    // Reset error state when navigating to a different stock so the
    // section heals automatically without a hard page refresh.
    if (this.state.hasError && prevProps.symbol !== this.props.symbol) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error, info) {
    // Local-only logging — DO NOT rethrow. Deep Analysis errors must
    // never affect the rest of the page.
    // eslint-disable-next-line no-console
    console.error("DeepAnalysisSection crashed:", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Card
        className="border-2 border-amber-500/40 bg-amber-500/5"
        data-testid="deep-analysis-error-boundary"
      >
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" />
            Deep Analysis temporarily unavailable
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            We hit an unexpected error rendering the Deep Analysis section.
            The rest of this page is unaffected — you can keep using it
            normally. Try again in a moment.
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={this.handleReset}
              data-testid="deep-analysis-error-retry"
            >
              <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
              Try again
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => window.location.reload()}
              data-testid="deep-analysis-error-refresh"
            >
              Refresh page
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
}

export default DeepAnalysisErrorBoundary;
